import React, { useState, useEffect, useRef } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import SignatureCanvas from 'react-signature-canvas';
import { useToast } from '../context/ToastContext.js';
import PdfFormFiller from './PdfFormFiller.jsx';
import { isFillablePdf } from '../utils/pdfFormTemplate.js';
import './WaiverSigning.css';

// ============================================================
// WAIVER SIGNING COMPONENT (Parent-Facing)
// ============================================================

function WaiverSigningPortal() {
  const toast = useToast();
  const [step, setStep] = useState(1); // 1: Select Athlete, 2: View Forms, 3: Sign
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [requiredForms, setRequiredForms] = useState([]);
  const [currentForm, setCurrentForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  
  const signatureRef = useRef(null);

  // The signed-in parent's name, used to pre-fill the printed-name field.
  const signerName = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('ams_user') || '{}');
      return u?.name || u?.user?.name || '';
    } catch {
      return '';
    }
  })();

  useEffect(() => {
    fetchParentAthletes();
  }, []);

  const fetchParentAthletes = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('ams_user') || '{}');
      const res = await api.get(`/api/parent/${user.id}/children`);
      // The children endpoint returns athlete_id/athlete_name/athlete_dob — normalise
      // to the id/name/dob this component uses.
      const kids = (Array.isArray(res.data) ? res.data : []).map(c => ({
        id: c.athlete_id ?? c.id,
        name: c.athlete_name ?? c.name ?? 'Athlete',
        dob: c.athlete_dob ?? c.dob ?? null,
      }));
      setAthletes(kids);
    } catch (err) {
      console.error('Error fetching athletes:', err);
    }
  };

  const selectAthlete = async (athlete) => {
    setSelectedAthlete(athlete);
    setLoading(true);

    try {
      // Template-driven compliance: returns a row per required template, with
      // status 'missing' for ones this athlete hasn't signed yet.
      const complianceRes = await api.get(`/api/forms/athlete/${athlete.id}/compliance`);

      // Filter for missing or expired waivers
      const missingForms = complianceRes.data.filter(
        wc => wc.status === 'missing' || wc.status === 'expired' || wc.status === 'expiring_soon'
      );

      // Fetch each template by its OWN id. Looking it up by form_type and taking
      // the first result presented the wrong document whenever a club had two
      // forms of the same type — e.g. two 'waiver' forms, one of which was already
      // signed. The compliance row already carries the exact template id.
      const formResponses = await Promise.all(
        missingForms.map(wc =>
          api.get(`/api/forms/templates/${wc.form_template_id}`).catch(() => null)
        )
      );
      const forms = formResponses
        .map(res => res?.data)
        .filter(Boolean)
        // Present them in the order the club chose, same as the kiosk.
        .sort((a, b) =>
          (a.display_order ?? 0) - (b.display_order ?? 0) ||
          String(a.form_name).localeCompare(String(b.form_name))
        );

      setRequiredForms(forms);
      setStep(2);
    } catch (err) {
      console.error('Error fetching forms:', err);
      toast.error('Error loading required forms');
    } finally {
      setLoading(false);
    }
  };

  const startFormSigning = (form) => {
    setCurrentForm(form);
    setFormData({});
    
    // Parse form fields if JSON string
    if (form.form_fields && typeof form.form_fields === 'string') {
      try {
        form.form_fields = JSON.parse(form.form_fields);
      } catch (e) {
        console.error('Error parsing form fields:', e);
      }
    }
    
    setStep(3);
  };

  const handleFormFieldChange = (fieldName, value) => {
    setFormData({
      ...formData,
      [fieldName]: value
    });
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  const submitSignedWaiver = async () => {
    try {
      if (!signatureRef.current || signatureRef.current.isEmpty()) {
        toast.error('Please provide your signature');
        return;
      }

      const signatureData = signatureRef.current.toDataURL();
      const signatureName = formData.signature_name || formData.parent_name;

      // Inline /api/forms/submit expects snake_case; parent_id + date derived server-side
      await api.post(`/api/forms/submit`, {
        form_template_id: currentForm.id,
        athlete_id: selectedAthlete.id,
        form_data: formData,
        signature_data: signatureData,
        signature_name: signatureName
      });

      toast.success('Waiver signed successfully!');
      
      // Remove from required forms list
      setRequiredForms(requiredForms.filter(f => f.id !== currentForm.id));
      
      // If no more forms, go back to athlete selection
      if (requiredForms.length <= 1) {
        setStep(1);
        setSelectedAthlete(null);
      } else {
        setStep(2);
      }

      setCurrentForm(null);
      setFormData({});

    } catch (err) {
      console.error('Error submitting waiver:', err);
      toast.error('Failed to submit waiver');
    }
  };

  const renderFormFields = () => {
    if (!currentForm) return null;

    // If PDF-based waiver, show PDF viewer
    if (currentForm.pdf_file_path) {
      return (
        <div className="pdf-waiver-viewer">
          <h3>Please review the waiver document:</h3>
          <iframe
            src={`${API_BASE_URL}/${currentForm.pdf_file_path}`}
            title="Waiver Document"
            width="100%"
            height="600px"
            style={{ border: '1px solid #ddd', borderRadius: '8px', marginBottom: '20px' }}
          />
          <div className="form-group">
            <label>Parent/Guardian Full Name *</label>
            <input
              type="text"
              value={formData.parent_name || ''}
              onChange={(e) => handleFormFieldChange('parent_name', e.target.value)}
              placeholder="Enter your full legal name"
              required
            />
          </div>
        </div>
      );
    }

    // Otherwise render dynamic form fields
    if (!currentForm.form_fields) return null;

    let fields;
    try {
      fields = typeof currentForm.form_fields === 'string' 
        ? JSON.parse(currentForm.form_fields) 
        : currentForm.form_fields;
    } catch (e) {
      console.error('Error parsing form fields:', e);
      return null;
    }

    return fields.map((field, index) => {
      switch (field.type) {
        case 'text':
          return (
            <div key={index} className="form-group">
              <label>{field.label} {field.required && '*'}</label>
              <input
                type="text"
                value={formData[field.name] || ''}
                onChange={(e) => handleFormFieldChange(field.name, e.target.value)}
                required={field.required}
              />
            </div>
          );

        case 'textarea':
          return (
            <div key={index} className="form-group">
              <label>{field.label} {field.required && '*'}</label>
              <textarea
                value={formData[field.name] || ''}
                onChange={(e) => handleFormFieldChange(field.name, e.target.value)}
                required={field.required}
                rows="3"
              />
            </div>
          );

        case 'checkbox':
          return (
            <div key={index} className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData[field.name] || false}
                  onChange={(e) => handleFormFieldChange(field.name, e.target.checked)}
                  required={field.required}
                />
                {' '}{field.label} {field.required && '*'}
              </label>
            </div>
          );

        case 'select':
          return (
            <div key={index} className="form-group">
              <label>{field.label} {field.required && '*'}</label>
              <select
                value={formData[field.name] || ''}
                onChange={(e) => handleFormFieldChange(field.name, e.target.value)}
                required={field.required}
              >
                <option value="">Select...</option>
                {field.options.map((opt, i) => (
                  <option key={i} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          );

        case 'date':
          return (
            <div key={index} className="form-group">
              <label>{field.label} {field.required && '*'}</label>
              <input
                type="date"
                value={formData[field.name] || ''}
                onChange={(e) => handleFormFieldChange(field.name, e.target.value)}
                required={field.required}
              />
            </div>
          );

        default:
          return null;
      }
    });
  };

  return (
    <div className="waiver-signing-portal">
      <div className="portal-header">
        <h1>📝 Digital Waivers & Forms</h1>
        {selectedAthlete && (
          <p className="selected-athlete">Signing for: <strong>{selectedAthlete.name}</strong></p>
        )}
      </div>

      {/* Step 1: Select Athlete */}
      {step === 1 && (
        <div className="athlete-selection">
          <h2>Select Athlete to Sign Forms</h2>
          <div className="athletes-grid">
            {athletes.map(athlete => (
              <div key={athlete.id} className="athlete-card" onClick={() => selectAthlete(athlete)}>
                <div className="athlete-avatar">
                  {(athlete.name || '?').split(' ').map(n => n[0]).join('')}
                </div>
                <h3>{athlete.name}</h3>
                <p>DOB: {athlete.dob || '—'}</p>
                <button className="btn-primary">View Required Forms</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: View Required Forms */}
      {step === 2 && (
        <div className="forms-list">
          <button onClick={() => setStep(1)} className="btn-back">← Back to Athletes</button>
          
          <h2>Required Forms for {selectedAthlete?.name}</h2>
          
          {loading ? (
            <div className="loading">Loading forms...</div>
          ) : requiredForms.length === 0 ? (
            <div className="success-message">
              ✅ All required forms are signed and up to date!
            </div>
          ) : (
            <div className="forms-grid">
              {requiredForms.map(form => (
                <div key={form.id} className="form-card">
                  <div className="form-header">
                    <h3>{form.form_name}</h3>
                    <span className="form-badge">{form.form_type}</span>
                  </div>
                  <p className="form-description">
                    {form.requires_signature ? '✍️ Signature Required' : ''}
                    {form.requires_annual_renewal ? ' • 📅 Annual Renewal' : ''}
                  </p>
                  <button 
                    onClick={() => startFormSigning(form)} 
                    className="btn-primary"
                  >
                    Sign Form →
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 3a: a mapped PDF — filled in and signed on the document itself,
          prefilled from what the gym already holds. Same component the kiosk uses,
          so a form behaves identically at home and at the front desk. */}
      {step === 3 && currentForm && isFillablePdf(currentForm) && (
        <div className="form-signing">
          <button onClick={() => setStep(2)} className="btn-back">← Back to Forms</button>
          <div className="form-container">
            <h2>{currentForm.form_name}</h2>
            <span className="form-type-badge">{currentForm.form_type}</span>
            <PdfFormFiller
              template={currentForm}
              athleteId={selectedAthlete.id}
              signerName={signerName}
              onSigned={(result) => {
                if (result && result.hasSignedPdf === false) {
                  toast.error(
                    `${currentForm.form_name}: signature saved, but the filled PDF could not be generated. Please tell the gym.`
                  );
                } else {
                  toast.success(`${currentForm.form_name} signed ✓`);
                }
                const remaining = requiredForms.filter(f => f.id !== currentForm.id);
                setRequiredForms(remaining);
                setCurrentForm(null);
                setFormData({});
                if (remaining.length === 0) {
                  setStep(1);
                  setSelectedAthlete(null);
                } else {
                  setStep(2);
                }
              }}
              onCancel={() => setStep(2)}
            />
          </div>
        </div>
      )}

      {/* Step 3b: written content, or a PDF that has not been mapped yet */}
      {step === 3 && currentForm && !isFillablePdf(currentForm) && (
        <div className="form-signing">
          <button onClick={() => setStep(2)} className="btn-back">← Back to Forms</button>

          <div className="form-container">
            <h2>{currentForm.form_name}</h2>
            <span className="form-type-badge">{currentForm.form_type}</span>

            {/* Form Content */}
            <div className="form-content">
              <div 
                dangerouslySetInnerHTML={{ __html: currentForm.content }}
                className="legal-text"
              />
            </div>

            {/* Custom Form Fields */}
            <div className="form-fields">
              <h3>Required Information</h3>
              {renderFormFields()}

              {/* Parent/Guardian Name for Signature */}
              <div className="form-group">
                <label>Parent/Guardian Name *</label>
                <input
                  type="text"
                  value={formData.signature_name || ''}
                  onChange={(e) => handleFormFieldChange('signature_name', e.target.value)}
                  placeholder="Full Name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Today's Date</label>
                <input
                  type="text"
                  value={new Date().toLocaleDateString()}
                  disabled
                />
              </div>
            </div>

            {/* Signature Pad */}
            {currentForm.requires_signature && (
              <div className="signature-section">
                <h3>Signature *</h3>
                <p className="signature-instructions">
                  Please sign below using your mouse or touch screen
                </p>
                <div className="signature-pad-container">
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className: 'signature-canvas',
                      width: 500,
                      height: 200
                    }}
                  />
                </div>
                <button onClick={clearSignature} className="btn-secondary btn-small">
                  Clear Signature
                </button>
              </div>
            )}

            {/* Submit */}
            <div className="form-actions">
              <button onClick={() => setStep(2)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={submitSignedWaiver} className="btn-primary btn-large">
                ✍️ Submit Signed Form
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WaiverSigningPortal;
